import type { ReactNode } from "react";

/** Loading / error / empty state primitives (Req 26). Accessible + consistent. */

const panel = {
  background: "#fff",
  border: "1px solid #e7ecf2",
  borderRadius: 12,
  boxShadow: "0 1px 2px rgba(16,24,40,0.04), 0 1px 3px rgba(16,24,40,0.06)",
} as const;

export function LoadingState({ label = "Loading…" }: { label?: string }): ReactNode {
  return (
    <div role="status" aria-live="polite" style={{ ...panel, padding: "2.5rem", color: "#566472", textAlign: "center" }}>
      {label}
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }): ReactNode {
  return (
    <div role="alert" style={{ padding: "1.25rem 1.5rem", border: "1px solid #f3c2c2", background: "#fdf3f3", borderRadius: 12, color: "#8a1f1f" }}>
      <strong>Something went wrong.</strong>
      <div style={{ marginTop: 4, fontSize: "0.9rem" }}>{message}</div>
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          style={{ marginTop: 12, background: "#8a1f1f", color: "#fff", border: "none", borderRadius: 8, padding: "6px 14px", cursor: "pointer", fontWeight: 600 }}
        >
          Retry
        </button>
      ) : null}
    </div>
  );
}

export function EmptyState({ title, hint }: { title: string; hint?: string }): ReactNode {
  return (
    <div style={{ ...panel, padding: "3rem 2rem", textAlign: "center", color: "#5f6c79" }}>
      <div style={{ fontWeight: 700, color: "#17212b", fontSize: "1.05rem" }}>{title}</div>
      {hint ? <div style={{ marginTop: 6, fontSize: "0.9rem", maxWidth: 420, marginInline: "auto" }}>{hint}</div> : null}
    </div>
  );
}

/** "Coming next" marker for navigation targets not yet built (no fake pages). */
export function ComingNext({ feature }: { feature: string }): ReactNode {
  return (
    <div style={{ padding: "2rem", color: "#777" }}>
      <h2 style={{ marginTop: 0 }}>{feature}</h2>
      <p>This area is coming next. The current build focuses on the Migration Economics slice.</p>
    </div>
  );
}

/** Placeholder cards while a screen loads: keeps the layout steady and tells assistive tech it is busy. */
export function SkeletonCards({ count = 4, label = "Loading" }: { count?: number; label?: string }): ReactNode {
  return (
    <div role="status" aria-busy="true" aria-label={label} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 14 }}>
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="cfo-skeleton" style={{ height: 96, borderRadius: 12, background: "linear-gradient(90deg,#eef2f6,#f7f9fb,#eef2f6)", backgroundSize: "200% 100%", animation: "cfo-shimmer 1.4s linear infinite" }} />
      ))}
      <span style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}>{label}</span>
    </div>
  );
}
